import * as chrono from 'chrono-node';
import { addDays, format, isFriday, isValid, nextFriday, parseISO } from 'date-fns';
import type { SourceDateEntry } from '@creative-kit/shared';

/** Maximum years difference from reference date for valid parsed dates */
const MAX_DATE_YEARS_DIFF = 10;

export interface ParsedDateResult {
  date: Date;
  hasTime: boolean;
}

export interface QuickDateOption {
  id: 'tomorrow' | 'end-of-week' | 'in-one-week';
  label: string;
  date: Date;
  inputText: string;
}

export const parseNaturalLanguageDate = (
  input: string,
  refDate: Date = new Date(),
): ParsedDateResult | null => {
  const cleanedInput = input.trim().toLowerCase();
  if (!cleanedInput) return null;

  // Handle special cases that chrono-node might not recognize well
  // "end of week" / "end of this week" / "eow" -> next Friday (or today if Friday)
  if (
    cleanedInput === 'end of week' ||
    cleanedInput === 'end of this week' ||
    cleanedInput === 'eow'
  ) {
    const endOfWeekDate = getEndOfWeekDate(refDate);
    return {
      date: endOfWeekDate,
      hasTime: false,
    };
  }

  // Try chrono-node parsing for other inputs
  const results = chrono.parse(cleanedInput, refDate);
  if (results.length === 0) return null;

  const result = results[0];
  const date = result.start.date();

  // Validate parsed date is reasonable (not too far from reference date)
  // Reject dates more than MAX_DATE_YEARS_DIFF years in the past or future
  const yearsDiff = Math.abs(date.getFullYear() - refDate.getFullYear());
  if (yearsDiff > MAX_DATE_YEARS_DIFF) {
    console.warn('Parsed date too far from reference date:', date, 'reference:', refDate);
    return null;
  }

  const hasTime = result.start.isCertain('hour') || result.start.isCertain('minute');

  return { date, hasTime };
};

export const formatQuickDatePreview = (date: Date, hasTime = false) => {
  if (hasTime) {
    return format(date, 'EEE, MMM d, h:mm a');
  }
  return format(date, 'EEE, MMM d');
};

const getEndOfWeekDate = (refDate: Date) => (isFriday(refDate) ? refDate : nextFriday(refDate));

/**
 * Parse flexible date strings from source data.
 * Handles Mikuta-style formatting ("25January 2026", "March 15") and
 * natural language ("today", "next week", "in 2 weeks").
 */
export const parseFlexibleDate = (input: string, refDate: Date = new Date()): Date | null => {
  if (!input || !input.trim()) return null;
  let cleaned = input.trim();

  // Fix Mikuta-style dates: "25January" -> "25 January", "January25" -> "January 25"
  cleaned = cleaned.replace(
    /(\d)(January|February|March|April|May|June|July|August|September|October|November|December)/gi,
    '$1 $2',
  );
  cleaned = cleaned.replace(
    /(January|February|March|April|May|June|July|August|September|October|November|December)(\d)/gi,
    '$1 $2',
  );

  const results = chrono.parse(cleaned, refDate);
  if (results.length === 0) return null;

  const date = results[0].start.date();
  const yearsDiff = Math.abs(date.getFullYear() - refDate.getFullYear());
  if (yearsDiff > MAX_DATE_YEARS_DIFF) return null;

  return date;
};

/** Convert a stored YYYY-MM-DD ISO string to a human-readable display string */
export function isoToDisplay(iso: string): string {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    return isValid(d) ? format(d, 'MMM d, yyyy') : iso;
  } catch {
    return iso;
  }
}

/**
 * Parse a stored multi-date cell value into SourceDateEntry objects.
 * New format: JSON array "[{startDate, endDate?, label?}, ...]"
 * Legacy format: comma-separated ISO dates "YYYY-MM-DD,YYYY-MM-DD"
 */
export function parseMultiDateValue(value: string | undefined): SourceDateEntry[] {
  if (!value) return [];

  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (e): e is SourceDateEntry => typeof e === 'object' && typeof e.startDate === 'string',
        );
      }
    } catch {
      // Fall through to legacy parsing
    }
  }

  // Legacy: comma-separated ISO dates
  return value
    .split(',')
    .map((iso) => iso.trim())
    .filter(Boolean)
    .map((startDate) => ({ startDate }));
}

/** Serialize SourceDateEntry[] to a Firestore-safe string. */
export function formatMultiDateValue(entries: SourceDateEntry[]): string {
  if (entries.length === 0) return '';
  return JSON.stringify(entries);
}

/**
 * Like parseNaturalLanguageDate but also detects date ranges.
 * "apr 3 to apr 5" → { startDate: "2026-04-03", endDate: "2026-04-05" }
 * "next friday"    → { startDate: "2026-04-04" }
 */
export interface ParsedDateRange {
  startDate: string; // "YYYY-MM-DD"
  endDate?: string; // "YYYY-MM-DD" — present when input describes a range
}

export const parseNaturalLanguageDateRange = (
  input: string,
  refDate: Date = new Date(),
): ParsedDateRange | null => {
  const cleanedInput = input.trim().toLowerCase();
  if (!cleanedInput) return null;

  if (
    cleanedInput === 'end of week' ||
    cleanedInput === 'end of this week' ||
    cleanedInput === 'eow'
  ) {
    return { startDate: format(getEndOfWeekDate(refDate), 'yyyy-MM-dd') };
  }

  const results = chrono.parse(cleanedInput, refDate);
  if (results.length === 0) return null;

  const result = results[0];
  const start = result.start.date();
  if (Math.abs(start.getFullYear() - refDate.getFullYear()) > MAX_DATE_YEARS_DIFF) return null;

  const startDate = format(start, 'yyyy-MM-dd');

  if (result.end) {
    const end = result.end.date();
    if (Math.abs(end.getFullYear() - refDate.getFullYear()) <= MAX_DATE_YEARS_DIFF) {
      return { startDate, endDate: format(end, 'yyyy-MM-dd') };
    }
  }

  return { startDate };
};

export const getQuickDateOptions = (refDate: Date = new Date()): QuickDateOption[] => [
  {
    id: 'tomorrow',
    label: 'Tomorrow',
    date: addDays(refDate, 1),
    inputText: 'tomorrow',
  },
  {
    id: 'end-of-week',
    label: 'End of this week',
    date: getEndOfWeekDate(refDate),
    inputText: 'end of this week',
  },
  {
    id: 'in-one-week',
    label: 'In one week',
    date: addDays(refDate, 7),
    inputText: 'in one week',
  },
];
